import React from "react";
import { useParams } from "react-router-dom";
import RetreatListing from "../components/RetreatListing";
import Seo from "../components/Seo";
import { slugToLocation, getLocationIntro } from "../lib/locations";
import { HERO_IMAGE_URL } from "../lib/heroImage";
import { DEDICATED_PAGE_TYPES } from "../lib/courseTypes";
import { Sparkles, MapPin } from "lucide-react";

function LocationPage() {
  const { slug } = useParams();
  const location = slugToLocation(slug);
  const intro = getLocationIntro(location);

  const title = `Yoga Retreats in ${location}`;
  const description = `Find and compare yoga retreats, meditation retreats, wellness programs and teacher training courses in ${location}, India. Compare locations, dates, meals, reviews and prices.`;
  const canonicalPath = `/yoga-retreats-in/${slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Place",
        name: location,
        description: intro,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://yogaretreatsbooking.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: title,
            item: `https://yogaretreatsbooking.com${canonicalPath}`,
          },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pt-16">
      <Seo
        title={title}
        description={description}
        canonicalPath={canonicalPath}
        ogImagePath="/favicon.ico"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <div className="relative h-[340px] bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE_URL}
            alt={`Yoga retreats in ${location}`}
            className="w-full h-full object-cover opacity-60"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/50 to-gray-900" />
        </div>

        <div className="relative container mx-auto px-4 h-full flex flex-col justify-center items-center text-center">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold uppercase tracking-widest mb-4 border border-blue-500/30 backdrop-blur-md">
              <MapPin size={14} /> {location}, India
            </span>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
              Yoga Retreats in {location}
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">{intro}</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-10 relative z-10 pb-20">
        <RetreatListing
          lockedLocation={location}
          excludedTypes={DEDICATED_PAGE_TYPES}
          heading={`Yoga & Wellness Retreats in ${location}`}
          subheading={`Compare retreats in ${location} by style, duration, rating and price`}
        />

        <section className="mt-12 bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles size={20} className="text-blue-600" /> About Yoga Retreats in {location}
          </h2>
          <p className="text-gray-600 leading-relaxed">{intro}</p>
        </section>
      </div>
    </div>
  );
}

export default LocationPage;
